'use client';

import type { ButtonProps } from '@magicschool/spellbook/components/Button';
import { extractValuesFromFilterChipSelectItemSlots } from '@magicschool/spellbook/components/FilterChipSelect/extractValuesFromFilterChipSelectItemSlots';
import { FilterChipSelectProvider } from '@magicschool/spellbook/components/FilterChipSelect/FilterChipSelectProvider';
import {
  FilterChipSelectContentSlot,
  FilterChipSelectGroupSlot,
  FilterChipSelectHeadingSlot,
  FilterChipSelectItemDescriptionSlot,
  FilterChipSelectItemLabelSlot,
  FilterChipSelectItemLeadingIconSlot,
  FilterChipSelectItemSlot,
  FilterChipSelectItemTrailingIconSlot,
  FilterChipSelectRightPanelSlot,
  FilterChipSelectTooltipContentSlot,
  FilterChipSelectTooltipSlot,
  FilterChipSelectTriggerLeadingIconSlot,
  FilterChipSelectTriggerSlot,
} from '@magicschool/spellbook/components/FilterChipSelect/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { useDirection } from 'radix-ui/direction';
import * as DropdownMenuPrimitive from 'radix-ui/dropdown-menu';
import { useCallback, useMemo, useState } from 'react';

export type FilterChipSelectProps = Omit<React.ComponentProps<typeof DropdownMenuPrimitive.Root>, 'dir' | 'modal'> & {
  'data-testid'?: string;
  /** The selection type.  */
  multiple?: boolean;
  /** The currently selected value(s). */
  value?: string | string[];
  /** The default selected value(s) when uncontrolled. */
  defaultValue?: string | string[];
  /** Callback when selection changes. This callback is overloaded to handle both single and multi selection. Single-select passes `undefined` when the active item is deselected. */
  onCheckedChange?: ((value: string | undefined) => void) | ((value: string[]) => void);
  /** Whether the dropdown is open by default. */
  open?: boolean;
  /** The maximum number of items to show before truncating with "+#". Ignored when type is 'single'. */
  maxItemsToShow?: number;
  /** The size of the button. `xs` is excluded — this component's badge-size mapping only supports the `sm`/`md`/`lg` tiers it's documented for. */
  size?: Exclude<ButtonProps['size'], 'xs'>;
  id?: string;
  disabled?: boolean;
};
const FilterChipSelectBase: React.FC<FilterChipSelectProps> = ({
  children,
  'data-testid': dataTestId,
  defaultOpen,
  open: providedOpen,
  onOpenChange,
  multiple = true,
  id,
  value: providedValue,
  defaultValue: providedDefaultValue,
  onCheckedChange,
  maxItemsToShow = 1,
  size = 'md',
  disabled,
  ...props
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen || false);
  const dir = useDirection();

  const isControlled = providedOpen !== undefined;
  const open = isControlled ? providedOpen : internalOpen;

  const normalizeValue = useCallback(
    (value: string | string[] | undefined, propName: 'value' | 'defaultValue'): string | string[] | undefined => {
      if (value === undefined) {
        return undefined;
      }

      if (!multiple) {
        if (Array.isArray(value)) {
          console.error(
            `FilterChipSelect: Type mismatch - 'type' is set to 'single' but '${propName}' is an array. Please provide a string ${propName} when type is 'single'.`,
          );
          return value.length > 0 ? value[0] : undefined;
        }
        return value;
      } else {
        if (typeof value === 'string') {
          console.error(
            `FilterChipSelect: Type mismatch - 'type' is set to 'multi' but '${propName}' is a string. Please provide an array of strings when type is 'multi'.`,
          );
          return [value];
        }
        return value;
      }
    },
    [multiple],
  );

  const normalizedValue = useMemo(() => normalizeValue(providedValue, 'value'), [normalizeValue, providedValue]);
  const normalizedDefaultValue = useMemo(
    () => normalizeValue(providedDefaultValue, 'defaultValue'),
    [normalizeValue, providedDefaultValue],
  );

  const handleCheckedChange = useMemo(() => {
    if (!onCheckedChange) return undefined;

    return (newValue: string | string[] | undefined) => {
      if (!multiple) {
        if (newValue === undefined) {
          (onCheckedChange as (value: string | undefined) => void)(undefined);
          return;
        }
        const singleValue = typeof newValue === 'string' ? newValue : Array.isArray(newValue) && newValue.length > 0 ? newValue[0] : '';
        if (typeof singleValue === 'string') {
          (onCheckedChange as (value: string | undefined) => void)(singleValue);
        }
      } else {
        const multiValue = Array.isArray(newValue) ? newValue : newValue !== undefined ? [newValue] : [];

        (onCheckedChange as (value: string[]) => void)(multiValue);
      }
    };
  }, [onCheckedChange, multiple]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  const contextValue = useMemo(
    () => ({
      isOpen: open,
      setOpen: handleOpenChange,
      multiple,
      value: normalizedValue,
      defaultValue: normalizedDefaultValue,
      onCheckedChange: handleCheckedChange,
      maxItemsToShow: maxItemsToShow,
      options: extractValuesFromFilterChipSelectItemSlots(children),
    }),
    [open, handleOpenChange, multiple, normalizedValue, normalizedDefaultValue, handleCheckedChange, maxItemsToShow, children],
  );

  const { slots } = useSlots(children, {
    trigger: {
      Component: FilterChipSelectTriggerSlot,
      props: { 'data-testid': dataTestId, dir, defaultValue: normalizedDefaultValue, value: normalizedValue, open, size, id, disabled },
    },
    content: { Component: FilterChipSelectContentSlot, props: { dir, ...props } },
    tooltip: {
      Component: FilterChipSelectTooltipSlot,
      props: { 'data-testid': dataTestId, dir, defaultValue: normalizedDefaultValue, value: normalizedValue, open, size, disabled },
    },
  });

  return (
    <FilterChipSelectProvider value={contextValue}>
      <DropdownMenuPrimitive.Root dir={dir} open={open} onOpenChange={handleOpenChange} modal defaultOpen={defaultOpen} {...props}>
        {slots.tooltip ?? slots.trigger}
        {slots.content}
      </DropdownMenuPrimitive.Root>
    </FilterChipSelectProvider>
  );
};

/**
 * A filter chip that lets users select options from a dropdown. Displays selected values as chip labels
 * and supports both single and multiple selection.
 *
 * ### When to use
 * - Filtering a list or table by one or many options from a compact chip in a filter bar
 *
 * ### When NOT to use
 * - Selecting a value inside a form: use `Select` or `MultiSelect`
 * - A boolean filter: use `FilterChipToggle`
 * - A date filter: use `FilterChipDate`
 *
 * ### Slots
 * - `<FilterChipSelect.Trigger>`: The trigger button. Opinionated — not overridable.
 * - `<FilterChipSelect.TriggerLeadingIcon>`: (optional) Leading icon inside the trigger.
 * - `<FilterChipSelect.Content>`: The dropdown content container.
 * - `<FilterChipSelect.Item>`: A selectable item. Requires a `value` prop.
 * - `<FilterChipSelect.ItemLabel>`: Label text for an item. Highly recommended.
 * - `<FilterChipSelect.ItemDescription>`: (optional) Secondary text below the item label.
 * - `<FilterChipSelect.ItemLeadingIcon>`: (optional) Icon before the item label.
 * - `<FilterChipSelect.ItemTrailingIcon>`: (optional) Icon after the item label.
 * - `<FilterChipSelect.Group>`: Groups items with an automatic separator. Multi-select only.
 * - `<FilterChipSelect.Heading>`: Label for a group of items. Use inside `<FilterChipSelect.Group>`.
 * - `<FilterChipSelect.Tooltip>`: (optional) Tooltip shown on trigger hover. Not overridable.
 * - `<FilterChipSelect.TooltipContent>`: (optional) Text content of the tooltip.
 * - `<FilterChipSelect.RightPanel>`: (optional) A panel rendered beside the item list for previews or
 *   contextual content. Place inside `<FilterChipSelect.Content>`. Not part of the ARIA menu tree.
 *   Use `useFilterChipSelect()` inside the panel to read `highlightedValue` and react to item focus/hover.
 *
 * ### Props
 * - `multiple`: `true` for multi-select (default), `false` for single-select.
 * - `value`: Controlled selected value(s). String for single-select, string array for multi-select.
 * - `defaultValue`: Initial selected value(s) for uncontrolled usage.
 * - `onCheckedChange`: Called when selection changes. Receives `string | undefined` (single) or `string[]` (multi).
 * - `maxItemsToShow`: Max selected labels shown on the trigger before truncating with "+N". Default `1`. Multi-select only.
 * - `open`: Controlled open state.
 * - `onOpenChange`: Called when the open state changes.
 * - `defaultOpen`: Initial open state for uncontrolled usage.
 * - `size`: Trigger button size — `'sm'`, `'md'` (default), or `'lg'`.
 * - `disabled`: Disables the trigger.
 *
 * @headless Radix DropdownMenu root renders no element; target `filter-chip-select-trigger` and `filter-chip-select-content`.
 */
export const FilterChipSelect = Object.assign(FilterChipSelectBase, {
  Trigger: FilterChipSelectTriggerSlot,
  TriggerLeadingIcon: FilterChipSelectTriggerLeadingIconSlot,
  Content: FilterChipSelectContentSlot,
  Tooltip: FilterChipSelectTooltipSlot,
  Item: FilterChipSelectItemSlot,
  ItemLabel: FilterChipSelectItemLabelSlot,
  ItemDescription: FilterChipSelectItemDescriptionSlot,
  ItemLeadingIcon: FilterChipSelectItemLeadingIconSlot,
  ItemTrailingIcon: FilterChipSelectItemTrailingIconSlot,
  Group: FilterChipSelectGroupSlot,
  Heading: FilterChipSelectHeadingSlot,
  TooltipContent: FilterChipSelectTooltipContentSlot,
  RightPanel: FilterChipSelectRightPanelSlot,
});
